import { lightState, voidRgba, warmRgba, type LightInputs } from '@/game/engine/lighting';

/**
 * The torch light. Three stacked layers, all driven by `game/engine/lighting`:
 *
 *   1. warm screen-blend  — the flame's colour, cooling with depth
 *   2. wet-rock sheen     — a faint specular sweep that grows deeper down
 *   3. vignette           — the darkness, tightened by low energy
 *
 * The radius is the lamp's `light_px` scaled by remaining fuel, so the cave
 * literally closes in as the torch burns down and opens up when you upgrade —
 * no separate "vision" number to keep in sync.
 *
 * Flicker is a CSS animation rather than a React state tick: a 60 fps setState
 * on a mobile WebView is a battery bill for something the compositor does free.
 * It animates opacity only — a transform would drag the light's centre around,
 * and the centre is where the miner is standing.
 *
 * **The layers straddle the rocks, and the split is deliberate.** The additive
 * pass (screen glow, overlay sheen) sits at `z-3`, under `RockField`; the
 * subtractive pass (the multiply cone and the vignette) sits at `z-5`, over it.
 * With everything above the rocks, `mix-blend-screen` at .52 alpha compressed
 * rock and wall toward the same value wherever the flame was brightest — a rock
 * standing in the light measured 1.3:1 against the wall behind it, so the one
 * thing the player has to aim at was least readable exactly where they were
 * looking. Light lands on the walls; darkness falls on everything, rocks
 * included, which is what keeps them in the scene.
 *
 * ## The dark is a colour now, not the absence of one
 *
 * Every subtractive stop here used to be `rgba(0,0,0,α)`. That is not "dim" — at
 * high α it is *nothing*, a region of screen with no information in it, and players
 * read that as the renderer having stopped rather than as a torch that only reaches
 * so far. It is why the vignette and the depth dimmer have each been walked back
 * once already, separately, by someone looking at a screen that appeared broken.
 *
 * Two changes end that argument instead of re-having it:
 *
 * - the shadows are painted in the **cave's own fog colour** (`L.voidRgb`, floored
 *   off black channel by channel), so the deepest shadow still carries the wall's
 *   shape and the Glass Reef's dark is blue where Emberfall's is warm;
 * - every alpha goes through **`L.shade()`**, which applies the player's brightness
 *   setting and clamps at `DARK_CEILING`. Fuel, depth and fatigue multiply together
 *   here and each was reasonable alone; the ceiling is what survives someone later
 *   tuning any one of them upward.
 */
export function TorchLight(props: LightInputs) {
  const L = lightState(props);
  const torchX = L.directionalX ?? '30%';
  const torchY = L.directionalY ?? '54%';
  const bounceX = '46%';
  const bounceY = '68%';

  // Full 100% physical brightness core scaling with lamp level and fuel
  const alphaWarm = Math.min(0.85, 0.52 * (L.beamIntensity ?? 1.0));
  const alphaGlow = Math.min(0.45, 0.22 * (L.beamIntensity ?? 1.0));
  const depthDim = L.depthDimmer ?? 0.0;
  /* One name for "the dark, as this cave paints it and this player set it". */
  const dark = (alpha: number) => voidRgba(L.voidRgb, L.shade(alpha));

  return (
    <>
      {/* 1. Physical Volumetric Light Cone: Left Torch Flame -> Right Tunnel Shadow */}
      <div
        className="pointer-events-none absolute inset-0 z-[5] mix-blend-multiply transition-all duration-500"
        style={{
          background:
            `linear-gradient(112deg, rgba(255,235,170,${(0.25 * (L.beamIntensity ?? 1)).toFixed(2)}) 0%,`
            + ` rgba(255,160,40,${(0.12 * (L.beamIntensity ?? 1)).toFixed(2)}) 32%,`
            + ` ${dark(0.06 + depthDim * 0.26)} 62%,`
            + ` ${dark(0.38 + depthDim * 0.28)} 100%)`,
        }}
        aria-hidden
      />

      {/* 2. Primary 100% Full Brightness Volumetric Torch Flame Core (Left Arm: 30% 54%) */}
      <div
        className="pointer-events-none absolute inset-0 z-[3] mix-blend-screen transition-[background] duration-500"
        style={{
          background: `radial-gradient(ellipse ${L.glowRadius * 1.5}px ${L.glowRadius * 1.15}px at ${torchX} ${torchY},
            ${warmRgba(L.warm, alphaWarm)} 0%, ${warmRgba(L.warm, alphaGlow)} 48%, transparent 85%)`,
          animation: `${L.guttering ? 'torchGutter' : 'torchCalm'} ${L.flickerSeconds}s ease-in-out infinite`,
        }}
        aria-hidden
      />

      {/* 3. Secondary Center Character Surface Diffuse Bounce (Center Body: 46% 68%) */}
      <div
        className="pointer-events-none absolute inset-0 z-[3] mix-blend-screen transition-[background] duration-500"
        style={{
          background: `radial-gradient(ellipse 280px 200px at ${bounceX} ${bounceY},
            ${warmRgba(L.warm, alphaWarm * 0.48)} 0%, ${warmRgba(L.warm, alphaGlow * 0.35)} 55%, transparent 85%)`,
        }}
        aria-hidden
      />

      {/* 4. Damp Rock Specular Sheen (Deepens with Cave Depth) */}
      {L.sheen > 0.08 && (
        <div
          className="pointer-events-none absolute inset-0 z-[3] mix-blend-overlay transition-opacity duration-700"
          style={{
            background: `radial-gradient(ellipse 80% 50% at ${torchX} ${torchY},
              rgba(190,225,255,${(L.sheen * 0.65).toFixed(3)}) 0%, transparent 80%)`,
            opacity: L.sheen,
          }}
          aria-hidden
        />
      )}

      {/*
        * 5. Depth Ambient Dimmer & Low-Energy Fatigue Vignette Darkness
        *
        * Stops were 0.12 / 0.72 / 0.95, and compounded with fatigue and
        * depthDimmer on top — a mid-depth run with tired legs put the 80%
        * ring past total black, so a "the torch only reaches so far" cue
        * read as "the renderer stopped drawing". Lowered so the far edge
        * stays the darkest part of frame without erasing the rock and wall
        * sitting in it; the flame's own falloff (layer 1) still does the
        * work of making the middle distance read as dimmer than the core.
        *
        * They are now also floored in colour and capped in alpha by `dark()`,
        * which is what stops this from needing to be walked back a third time.
        */}
      <div
        className="pointer-events-none absolute inset-0 z-[5] transition-[background] duration-500"
        style={{
          background: `radial-gradient(circle ${L.radius * 1.1}px at ${torchX} ${torchY},
            ${dark(0)} 0%,
            ${dark(0.08 + L.fatigue * 0.32 + depthDim * 0.14)} 48%,
            ${dark(0.50 + L.fatigue * 0.28 + depthDim * 0.20)} 80%,
            ${dark(0.76 + depthDim * 0.10)} 100%)`,
        }}
        aria-hidden
      />
    </>
  );
}
